import { useTranslation } from '@nobia/zeus-components/lib/i18n/useTranslation'
import { useHistory } from '@nobia/zeus-components/lib/router'
import PropTypes from 'prop-types'
import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react'
import { toast } from 'react-toastify'

import { Connection } from '../../app/signalr'
import { useRoutes } from '../../routes'
import { useUser } from '../user-provider/UserProvider'

const SignalRContext = createContext({
  onNewMessage: () => {},
  offNewMessage: () => {},
})

export const useSignalR = () => useContext(SignalRContext)

export const SignalRProvider = ({ children }) => {
  const { t } = useTranslation()
  const { customer } = useUser()
  const history = useHistory()
  const { messages } = useRoutes()
  const listenersRef = useRef(new Set())

  const messagingEnabled = customer?.messagingEnabled || false

  // Notify all listeners about new message
  const notifyListeners = useCallback((data) => {
    listenersRef.current.forEach((callback) => {
      if (typeof callback === 'function') {
        callback(data)
      }
    })
  }, [])

  const onNewMessage = useCallback((callback) => {
    listenersRef.current.add(callback)
  }, [])

  const offNewMessage = useCallback((callback) => {
    listenersRef.current.delete(callback)
  }, [])

  const handleNewMessageRef = useRef(null)
  const handleReconnectRef = useRef(null)

  useEffect(() => {
    handleNewMessageRef.current = (data) => {
      if (
        data?.message?.status &&
        data?.message?.status.toLowerCase() === 'published' &&
        (data?.message?.source?.toLowerCase() === 'store' || data?.message?.source?.toLowerCase() === 'designer')
      ) {
        toast.info(t('app.components.messages.new-message-received'), {
          onClick: () => {
            history.push(messages.path)
          },
          style: { cursor: 'pointer' },
        })
      }

      notifyListeners(data)
    }

    handleReconnectRef.current = () => {
      notifyListeners({ type: 'reconnect' })
    }
  }, [t, history, messages.path, notifyListeners])

  const stableHandleNewMessage = useCallback((data) => {
    handleNewMessageRef.current?.(data)
  }, [])

  const stableHandleReconnect = useCallback(() => {
    handleReconnectRef.current?.()
  }, [])

  useEffect(() => {
    if (!messagingEnabled) return

    Connection.subscribe(stableHandleNewMessage)
    Connection.onReconnect(stableHandleReconnect)

    return () => {
      Connection.unsubscribe()
      Connection.offReconnect(stableHandleReconnect)
    }
  }, [messagingEnabled, stableHandleNewMessage, stableHandleReconnect])

  const value = {
    onNewMessage,
    offNewMessage,
  }

  return (
    <SignalRContext.Provider value={value}>
      {children}
    </SignalRContext.Provider>
  )
}

SignalRProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

